import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from "yup";
import { useFormik } from "formik";
import Typography from '@mui/material/Typography';
import ReactQuill from 'react-quill';
import { Grid, Skeleton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import 'react-quill/dist/quill.snow.css';
import FormikControl from "../../../../components/common/FormikControl";
import { BackNavIcon } from "../../../../../assets/common-svg/CommonSvg";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createDisclaimer, fetchAllDisclaimers, fetchdisclaimerRoles, updateDisclaimer } from '../../../../slices/disclaimer';
import { limit, page } from '../../../../../lib/form-builder/components/constant/constant';

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required").min(3, "Title must be at least 3 characters"),
  permissions: Yup.array().min(1, "Permission is required").required("Permission is required"),
  status: Yup.number().required("Status is required"),
  disclaimer_message: Yup.string()
    .required("Message is required")
    .test("is-not-empty", "Message must be at least 10 characters", value => {
      const stripped = value?.replace(/<[^>]*>/g, "").trim();
      return stripped?.length >= 10;
    }),
});

const QuillToolbar = () => (
  <div id="quill-toolbar">
    <span className="ql-formats">
      <select className="ql-header" defaultValue="" title="Heading">
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
        <option value="">Normal</option>
      </select>
    </span>
    <span className="ql-formats">
      <button className="ql-bold" title="Bold" />
      <button className="ql-italic" title="Italic" />
      <button className="ql-underline" title="Underline" />
      <button className="ql-strike" title="Strikethrough" />
    </span>
    <span className="ql-formats">
      <select className="ql-color" title="Text Color" />
    </span>
    <span className="ql-formats">
      <button className="ql-align" value="" title="Align Left" />
      <button className="ql-align" value="center" title="Align Center" />
      <button className="ql-align" value="right" title="Align Right" />
      <button className="ql-align" value="justify" title="Justify" />
    </span>
    <span className="ql-formats">
      <button className="ql-list" value="ordered" title="Ordered List" />
      <button className="ql-list" value="bullet" title="Bullet List" />
    </span>
    <span className="ql-formats">
      <button className="ql-link" title="Insert Link" />
      <button className="ql-image" title="Insert Image" />
    </span>
  </div>
);
const modules = {
  toolbar: {
    container: '#quill-toolbar',
  },
  clipboard: { matchVisual: false }
};

const formats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'align', 'list', 'bullet',
  'link', 'image'
];

const AddNewDisclaimer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const quillRef = useRef(null);
  const location = useLocation();
  const { roles, AllDisclaimers } = useSelector(state => state.disclaimer);
  const isViewMode = location.pathname.includes('/view/');
  const isEditMode = Boolean(id) && !isViewMode;
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    status: 1,
    title: '',
    permissions: [],
    disclaimer_message: ''
  });
  useEffect(() => {
    dispatch(fetchdisclaimerRoles());
    if (isEditMode || isViewMode) {
      dispatch(fetchAllDisclaimers({
        page: page,
        limit: limit,
        // search: '',
        title: '',
        sort_by: 'id',
        order_by: 'desc',
      }));
    }
  }, [dispatch, isEditMode, isViewMode]);
  const sanitizePermissions = (permissions) => {
    return Array.isArray(permissions) ? permissions.filter(id => id != null) : [];
  };
  const handleSubmit = async (values) => {
    if (isViewMode) return;
    try {
      setLoading(true);
      const cleanedValues = {
        ...values,
        permissions: values.permissions.filter(p => p !== null)
      };
      if (isEditMode) {
        // Update existing disclaimer
        await dispatch(updateDisclaimer({ id, data: cleanedValues })).unwrap();
      } else {
        // Create new disclaimer
        await dispatch(createDisclaimer(cleanedValues)).unwrap();
      }

      navigate('/bmr/master/disclaimers');
    }
    catch (err) {
      console.error('Error creating disclaimer:', err);
    } finally {
      setLoading(false);
    }
  };
  const formik = useFormik({
    initialValues,
    validationSchema: isViewMode ? undefined : validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const matchedDisclaimer = useMemo(() => {
    if (!isEditMode && !isViewMode) return null;
    const disclaimersList = AllDisclaimers?.data || [];
    if (!disclaimersList.length) return null;
    return disclaimersList.find(item => String(item.id) === String(id)) ?? null;

  }, [id, isEditMode, isViewMode, AllDisclaimers]);


  const permissionOptions = useMemo(() => {
    const baseOptions = roles?.map(role => ({
      value: role.id,
      label: role.title
    })) || [];
    if ((isViewMode || isEditMode) && matchedDisclaimer?.roles) {
      const existingRoleIds = new Set(baseOptions.map(o => o.value));
      const additionalOptions = matchedDisclaimer.roles
        .filter(role => !existingRoleIds.has(role.id))
        .map(role => ({
          value: role.id,
          label: role.title,
        }));
      return [...baseOptions, ...additionalOptions];
    }

    return baseOptions;
  }, [roles, isViewMode, isEditMode, matchedDisclaimer]);
  useEffect(() => {
    const needsData = isEditMode || isViewMode;
    if (!needsData) return;
    const fetchData = async () => {
      try {
        setIsDataLoading(true);
        if (matchedDisclaimer) {
          setInitialValues({
            title: matchedDisclaimer.title || '',
            permissions: sanitizePermissions(matchedDisclaimer.permissions),
            disclaimer_message: matchedDisclaimer.disclaimer_message || '',
            status: matchedDisclaimer.status ?? 1,
          });
        }
        else {
          setInitialValues({
            title: '',
            permissions: [],
            disclaimer_message: '',
            status: 1,
          });
        }
      } catch (err) {
        console.error('Error fetching disclaimer:', err);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode, isViewMode, matchedDisclaimer]);

  const isButtonDisabled = isViewMode || loading || formik.isSubmitting;
  const formOptions = useMemo(() => [
    {
      label: 'Status',
      type: 'select',
      name: 'status',
      required: true,
      disabled: isViewMode,
      options: [
        { value: 1, label: 'Active' },
        { value: 0, label: "In-Active" }
      ]
    },
    {
      label: 'Title',
      name: 'title',
      placeholder: "Enter Title",
      required: true,
      disabled: isViewMode,
    },
    {
      label: 'Permission',
      name: 'permissions',
      type: 'select',
      multiple: true,
      placeholder: "Select Role",
      required: true,
      disabled: isViewMode,
      options: permissionOptions,
    },

  ], [permissionOptions, isViewMode]);
  return (
    <div className="!p-6">
      <button
        className="cursor-pointer flex gap-2"
        onClick={() => navigate('/bmr/master/disclaimers')}
      >
        <BackNavIcon /> Back
      </button>

      <div className="!p-4 mt-3 bg-white flex flex-col gap-4 rounded-2xl">
        <Typography variant="h5">
          {isViewMode ? 'View Disclaimer' : (isEditMode ? 'Update Disclaimer' : 'Add New Disclaimer')}
        </Typography>

        {isDataLoading ? (
          <Skeleton variant="rectangular" width="100%" height={200} />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2} className="mb-3">
              {formOptions.map((props, key) => (
                <Grid key={key} item xs={12}>
                  <FormikControl formik={formik} {...props} />
                </Grid>
              ))}
            </Grid>

            <Typography className='pb-1'>
              Disclaimer Message <span className="text-red-500">*</span>
            </Typography>
            {isViewMode ? (
              <div className="border-2 rounded-2xl p-4" style={{ backgroundColor: '#f9f9f9', minHeight: '200px' }}>
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: formik.values.disclaimer_message || '<em>No content available</em>'
                  }}
                  style={{
                    padding: '0',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    lineHeight: '1.5'
                  }}
                />
              </div>
            ) : (
              <>
                <div className="border-2 rounded-2xl disclaimer-quill-wrapper">
                  <QuillToolbar />
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={formik.values.disclaimer_message}
                    onChange={(val) => {
                      formik.setFieldValue("disclaimer_message", val);
                    }}
                    onBlur={() => {
                      // setTimeout(() => {
                      formik.setFieldTouched("disclaimer_message", true, true);
                      // }, 100);
                    }}
                    modules={modules}
                    formats={formats}
                    placeholder="Write something amazing..."
                  />
                </div>
                {formik.touched.disclaimer_message && formik.errors.disclaimer_message && (
                  <Typography variant="body2" color="error">
                    {formik.errors.disclaimer_message}
                  </Typography>
                )}
              </>
            )}
            {!isViewMode && (
              <Grid container spacing={2} className="mt-3">
                <Grid item xs={12}>
                  <LoadingButton
                    loading={loading}
                    type="submit"
                    variant="contained"
                    disabled={isButtonDisabled}
                  >
                    {isEditMode ? 'Update' : 'Submit'}
                  </LoadingButton>
                </Grid>
              </Grid>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AddNewDisclaimer;
